'use client'

import React, { useEffect, useState } from 'react'
import type { PartnersShowcaseBlock as PartnersShowcaseBlockProps, Partner as PartnerDoc } from '@/payload-types'
import { PartnersShowcase, type Partner } from '@/components/PartnersShowcase'
import { createClientLogger } from '@/lib/logger/client'

const log = createClientLogger('PartnersShowcaseBlock')

export const PartnersShowcaseBlock: React.FC<PartnersShowcaseBlockProps> = ({
  title,
  subtitle,
  showTitle,
  variant,
  itemsPerView,
  partners: selectedPartners,
  featuredOnly,
  limit,
}) => {
  const [partnersData, setPartnersData] = useState<Partner[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchPartners() {
      // If no partners are explicitly selected, fetch from database
      if (!selectedPartners || selectedPartners.length === 0) {
        try {
          const params = new URLSearchParams()
          if (featuredOnly) params.set('featured', 'true')
          if (limit) params.set('limit', limit.toString())

          const response = await fetch(`/api/custom/partners?${params}`)
          if (!response.ok) {
            log.error('Failed to fetch partners')
            setLoading(false)
            return
          }

          const result = await response.json()
          const docs = result.docs || result

          // Transform the data to match our component interface
          const transformedPartners = docs.map((partner: PartnerDoc) => {
            const websiteUrl = partner.website || undefined
            let pageUrl: string | undefined = undefined

            if (partner.page) {
              const pageSlug = typeof partner.page === 'string' ? partner.page : partner.page?.slug
              if (pageSlug) pageUrl = `/${pageSlug}`
            }

            return {
              id: partner.id,
              name: partner.name,
              description: partner.description || undefined,
              logo: {
                url: typeof partner.logo === 'object' && partner.logo?.url ? partner.logo.url : '',
                alt: typeof partner.logo === 'object' && partner.logo?.alt ? partner.logo.alt : partner.name,
                width: typeof partner.logo === 'object' && partner.logo?.width ? partner.logo.width : undefined,
                height: typeof partner.logo === 'object' && partner.logo?.height ? partner.logo.height : undefined,
              },
              website: websiteUrl,
              page: pageUrl,
              featured: partner.featured || false,
              active: partner.active !== false,
              order: partner.order || 0,
            }
          }).filter((partner: Partner) => partner.logo.url && partner.active)

          setPartnersData(transformedPartners)
        } catch (error) {
          log.error({ error }, 'Error fetching partners:')
          setPartnersData([])
        }
      } else {
        // Use the selected partners passed as props
        const transformedPartners = selectedPartners
          .map((partner) => {
            if (typeof partner === 'string') return null

            const websiteUrl = partner.website || undefined
            let pageUrl: string | undefined = undefined

            if (partner.page) {
              const pageSlug = typeof partner.page === 'string' ? partner.page : partner.page?.slug
              if (pageSlug) pageUrl = `/${pageSlug}`
            }

            return {
              id: partner.id,
              name: partner.name,
              description: partner.description || undefined,
              logo: {
                url: typeof partner.logo === 'object' && partner.logo?.url ? partner.logo.url : '',
                alt: typeof partner.logo === 'object' && partner.logo?.alt ? partner.logo.alt : partner.name,
                width: typeof partner.logo === 'object' && partner.logo?.width ? partner.logo.width : undefined,
                height: typeof partner.logo === 'object' && partner.logo?.height ? partner.logo.height : undefined,
              },
              website: websiteUrl,
              page: pageUrl,
              featured: partner.featured || false,
              active: partner.active !== false,
              order: partner.order || 0,
            }
          })
          .filter(Boolean) as Partner[]

        setPartnersData(transformedPartners)
      }

      setLoading(false)
    }

    fetchPartners()
  }, [selectedPartners, featuredOnly, limit])

  if (loading || partnersData.length === 0) return null

  return (
    <PartnersShowcase
      partners={partnersData}
      title={title || undefined}
      subtitle={subtitle || undefined}
      showTitle={showTitle || undefined}
      variant={variant || undefined}
      itemsPerView={itemsPerView || undefined}
    />
  )
}